import { useEffect } from 'react';
import { getVersion } from '../../services/version';
import {
  addDays,
  addMilliseconds,
  DATE_FNS_TIME,
  HourMinuteFormatted,
  isValid,
  MILLIS_IN_MINUTE,
  MILLIS_IN_SECOND,
  parseDate,
} from '../../utils/dateUtil';
import {
  createBehaviorSubject,
  useBehaviorSubjectValue,
} from '../../utils/behaviorSubject';
import {
  VERSION_HASH,
  VERSION_CHECK_TRIGGER_INTERVAL_MINUTES,
  VERSION_CHECK_TRIGGER_TIME,
  VERSION_SYSTEM_INTERVAL_SECONDS,
} from '../../configs/version';

export type VersionUpdateCheckParams = {
  trigger: VersionCheckTrigger;
};

export type VersionUpdateCheckResult = {
  isVersionUpdated: boolean;
};

/**
 * This hook allows components to check for version updates based on specified trigger.
 *
 * It returns an object containing a boolean value indicating whether the version has been updated.
 *
 * It can be used in multiple components with the same or different triggers, and it ensures
 * that the version check is performed efficiently without redundant checks.
 */
export const useCheckVersionUpdate = ({
  trigger,
}: VersionUpdateCheckParams): VersionUpdateCheckResult => {
  useEffect(() => {
    registerVersionCheck(trigger);
  }, [trigger]);
  const versionState = useBehaviorSubjectValue(versionState$, [trigger]);
  return {
    isVersionUpdated: versionState.updatedForTrigger.includes(trigger),
  };
};

type VersionCheckTrigger = 'interval' | 'time';

type VersionConfig = {
  hash: string | undefined;
  /**
   * In milliseconds how often the regular version conditions check should be performed.
   * It is the check of whether the next check date for any trigger has passed and if so, performing the version check.
   */
  systemIntervalMillis: number;
  /**
   * In milliseconds how often the version check should be performed for interval trigger.
   * If it is undefined, the check is disabled.
   */
  intervalTriggerMillis: number | undefined;
  /**
   * When the version check should be performed for time trigger.
   * If it is undefined, the check is disabled.
   */
  timeTrigger: HourMinuteFormatted | undefined;
};

type VersionState = {
  updated: boolean;
  updatedForTrigger: VersionCheckTrigger[];
};

const nextCheckDateMap = new Map<VersionCheckTrigger, Date>();
const versionState$ = createBehaviorSubject<VersionState>({
  updated: false,
  updatedForTrigger: [],
});
let isVersionLoading = false;
let systemIntervalId: ReturnType<typeof setInterval>;
let versionConfig: VersionConfig;

/**
 * This function registers a trigger for checking version updates.
 *
 * If the trigger is not already registered, or if the version has not already been detected
 * as updated for that trigger, it calculates the next check date for that trigger based
 * on the version configuration and starts the regular version check process if it's not already running.
 *
 * It can be called multiple times with the same trigger, but it will only register the trigger once
 * and will not start multiple intervals for checking version updates.
 */
const registerVersionCheck = (trigger: VersionCheckTrigger) => {
  if (
    nextCheckDateMap.has(trigger) ||
    versionState$.getValue().updatedForTrigger.includes(trigger)
  ) {
    return;
  }
  const { hash } = getVersionConfig();
  if (hash === undefined) {
    return;
  }
  const nextCheckDate = generateNextCheckDate(trigger);
  if (!nextCheckDate) {
    return;
  }
  nextCheckDateMap.set(trigger, nextCheckDate);
  startSystemInterval();
};

/**
 * Calculates the next check date for the interval trigger by adding
 * the specified check interval (in milliseconds) to the current date and time.
 */
const getIntervalTriggerNextCheckDate = (intervalTriggerMillis: number): Date =>
  addMilliseconds(new Date(), intervalTriggerMillis);

/**
 * Calculates the next check date for the time trigger based
 * on the specified check time.
 */
const getTimeTriggerNextCheckDate = (
  timeTrigger: HourMinuteFormatted,
): Date => {
  let checkDate = parseDate(timeTrigger, DATE_FNS_TIME);
  if (checkDate <= new Date()) {
    checkDate = addDays(checkDate, 1);
  }
  return checkDate;
};

/**
 * This function generates the next check date for a given trigger
 * based on the version configuration.
 */
const generateNextCheckDate = (
  trigger: VersionCheckTrigger,
): Date | undefined => {
  const { intervalTriggerMillis, timeTrigger } = getVersionConfig();
  if (trigger === 'interval') {
    if (intervalTriggerMillis === undefined) {
      return undefined;
    }
    return getIntervalTriggerNextCheckDate(intervalTriggerMillis);
  } else if (trigger === 'time') {
    if (timeTrigger === undefined) {
      return undefined;
    }
    return getTimeTriggerNextCheckDate(timeTrigger);
  } else {
    throw new Error(`Unknown trigger for version update check: ${trigger}`);
  }
};

/**
 * This function starts (or does nothing if already started) a regular interval to check for version updates.
 *
 * On each interval tick, it checks if a version check is already in progress.
 * If not, it determines which triggers for version checks are due based on the current time and the next check dates.
 *
 * If there are any triggers that are due for a version check, it performs the version check by calling the getVersion service
 * (skipped if the version has already been detected as updated in a previous check).
 *
 * If the version hash has changed compared to the cached version hash, it updates the versionState$,
 * marking the version as updated and appending the due triggers to updatedForTrigger so each trigger is notified
 * only once its own check time has passed.
 *
 * If the version hash has not changed, it calculates and updates the next check dates for the triggers that were checked,
 * so they will trigger the next check when their new check time is reached.
 */
const startSystemInterval = () => {
  if (systemIntervalId) {
    return;
  }
  const { systemIntervalMillis } = getVersionConfig();
  systemIntervalId = setInterval(() => {
    if (isVersionLoading) {
      return;
    }
    const now = new Date();
    const passedTriggers = Array.from(nextCheckDateMap.entries())
      .filter(([_, nextCheckDate]) => now >= nextCheckDate)
      .map(([trigger]) => trigger);
    if (passedTriggers.length === 0) {
      return;
    }
    isVersionLoading = true;
    (async () => {
      try {
        const prevState = versionState$.getValue();
        let updated = prevState.updated;
        if (!updated) {
          // Only check for version update if it hasn't been detected as updated in the previous checks
          const { hash } = await getVersion();
          if (!hash) {
            console.error('Received empty hash from version endpoint');
            return;
          }
          updated = hash !== versionConfig.hash;
        }
        if (updated) {
          // If the version is updated, we want to keep track of all triggers for which the update was detected
          versionState$.next({
            updated: true,
            updatedForTrigger: Array.from(
              new Set([...prevState.updatedForTrigger, ...passedTriggers]),
            ),
          });
          // Clear the next check dates for the triggers that were checked and detected as updated, so they won't trigger another check
          for (const trigger of passedTriggers) {
            nextCheckDateMap.delete(trigger);
          }
        } else {
          // Update the next check dates for the triggers that were checked but the version was not updated
          for (const trigger of passedTriggers) {
            const nextCheckDate = generateNextCheckDate(trigger);
            if (nextCheckDate) {
              nextCheckDateMap.set(trigger, nextCheckDate);
            } else {
              nextCheckDateMap.delete(trigger);
            }
          }
        }
      } catch (error) {
        console.error('Error checking version update:', error);
      } finally {
        isVersionLoading = false;
      }
    })();
  }, systemIntervalMillis);
};

/**
 * This function retrieves the version configuration, which includes the version hash
 * and the intervals for checking version updates for both info banner and LCD/Kiosk.
 * It validates the configuration values and throws errors if they are invalid.
 * The configuration is cached after the first retrieval.
 */
const getVersionConfig = (): VersionConfig => {
  if (versionConfig) {
    return versionConfig;
  }
  const hash = VERSION_HASH;
  if (!hash) {
    versionConfig = {
      hash: undefined,
      systemIntervalMillis: 0,
      intervalTriggerMillis: undefined,
      timeTrigger: undefined,
    };
  } else {
    let timeTrigger: HourMinuteFormatted | undefined =
      VERSION_CHECK_TRIGGER_TIME;
    if (timeTrigger === '0') {
      timeTrigger = undefined;
    } else {
      const parsedValue = parseDate(timeTrigger, DATE_FNS_TIME);
      if (!isValid(parsedValue)) {
        throw new Error(
          `Invalid time format for VERSION_CHECK_TRIGGER_TIME: ${timeTrigger}. Expected format is "HH:mm".`,
        );
      }
    }
    versionConfig = {
      hash,
      systemIntervalMillis: VERSION_SYSTEM_INTERVAL_SECONDS * MILLIS_IN_SECOND,
      intervalTriggerMillis:
        VERSION_CHECK_TRIGGER_INTERVAL_MINUTES !== 0
          ? VERSION_CHECK_TRIGGER_INTERVAL_MINUTES * MILLIS_IN_MINUTE
          : undefined,
      timeTrigger,
    };
  }
  return versionConfig;
};
